import React, { useMemo, useState } from 'react';
import { AlertTriangle, BookMarked, CheckCircle2, HandCoins, Wallet } from 'lucide-react';
import { toast } from 'react-toastify';
import PhotoUpload from '../common/PhotoUpload';
import {
  readFinanceRecords,
  readHandLoanRecords,
  upsertFinanceRecord,
  upsertHandLoanRecord,
} from '../../lib/workspaceStore';
import { adminEntryDateOnly, adminEntryIso } from '../../lib/workspaceAsOf';
import { useTranslation } from '../../lib/i18n';
import { daysBetweenYmd, type MigratedSchedulePreview } from '../../lib/migrationSchedule';
import {
  buildMigratedFinanceData,
  buildMigratedHandData,
  dateLabel,
  handEmiForMigration,
  previewMigratedFinance,
  previewMigratedHand,
} from '../../lib/migrationRecords';

const num = (v: string) => {
  const n = parseFloat(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

const inr = (n: number) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="app-label">{label}</label>
      {children}
      {hint ? <p className="mt-1 text-[11px] text-zinc-500">{hint}</p> : null}
    </div>
  );
}

function PhotoField({
  value,
  onChange,
  customerId,
}: {
  value: string;
  onChange: (v: string) => void;
  customerId: string;
}) {
  return (
    <div className="md:col-span-2">
      <label className="app-label">Customer photo (optional)</label>
      <PhotoUpload
        value={value}
        onChange={onChange}
        customerId={customerId}
        label="Optional — add now or later from the customer's loan."
      />
    </div>
  );
}

/** Live view of what the software will treat as overdue once the loan is saved. */
function OverduePreview({
  preview,
  cutoverYmd,
  showPenalty,
}: {
  preview: MigratedSchedulePreview | null;
  cutoverYmd: string;
  showPenalty: boolean;
}) {
  if (!preview) {
    return (
      <p className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800/80 dark:text-zinc-300">
        Enter the next due date, EMIs still to collect and the EMI amount to see the schedule and overdue.
      </p>
    );
  }
  if (!preview.overdue.length) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/30 dark:text-emerald-100">
        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Nothing overdue at cutover ({dateLabel(cutoverYmd)}). First EMI in the software: {dateLabel(preview.firstDue)} · last EMI:{' '}
          {dateLabel(preview.lastDue)}.
        </span>
      </div>
    );
  }
  const n = preview.overdue.length;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-100">
      <p className="text-sm font-semibold">
        {n} EMI{n === 1 ? '' : 's'} overdue at cutover ({dateLabel(cutoverYmd)}): {inr(preview.overdueAmount)}
      </p>
      <p className="mt-0.5">
        Oldest unpaid EMI was due {dateLabel(preview.overdue[0].dueDate)} — {preview.oldestDays} days overdue.
      </p>
      <ul className="mt-1.5 space-y-0.5 tabular-nums">
        {preview.overdue.map((r) => (
          <li key={r.dueDate}>
            Due {dateLabel(r.dueDate)} · {r.days} days overdue
            {showPenalty ? ` · penalty ${inr(r.penalty)}` : ''}
          </li>
        ))}
      </ul>
      {showPenalty ? (
        <p className="mt-1.5">
          Overdue penalty at cutover ≈ {inr(preview.penaltyTotal)} (EMI × days ÷ 365 for each unpaid EMI). It keeps growing each
          day until the EMI is collected, like any other overdue loan.
        </p>
      ) : null}
      <p className="mt-1">
        Next upcoming EMI: {dateLabel(preview.nextUpcomingDue)} · last EMI: {dateLabel(preview.lastDue)}.
      </p>
    </div>
  );
}

export function MigrateFinanceForm({
  canWrite,
  cutoverDefault,
  onDone,
}: {
  canWrite: boolean;
  cutoverDefault: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [recordId] = useState(() => crypto.randomUUID());
  const [f, setF] = useState({
    cutoverDate: cutoverDefault,
    customerName: '',
    mobileNumber: '',
    regNo: '',
    haNumber: '',
    originalAgreementDate: '',
    originalPrincipal: '',
    originalTenureMonths: '',
    emisPaidBefore: '',
    outstandingPrincipal: '',
    interestDue: '',
    remainingMonths: '',
    overdueEmis: '',
    emiAmount: '',
    interestPercentage: '',
    nextDueDate: '',
    applicantPhoto: '',
    notes: '',
  });

  const remainingAuto = useMemo(() => {
    const tenure = Math.floor(num(f.originalTenureMonths));
    const paid = Math.floor(num(f.emisPaidBefore));
    if (tenure > 0 && paid >= 0) return Math.max(0, tenure - paid);
    return 0;
  }, [f.originalTenureMonths, f.emisPaidBefore]);

  const cutoverYmd = adminEntryDateOnly(f.cutoverDate);
  const overdueCount = Math.floor(num(f.overdueEmis));
  const remainingCount = Math.floor(num(f.remainingMonths) || remainingAuto);

  const preview = useMemo(
    () =>
      previewMigratedFinance({
        cutoverYmd,
        nextDueDate: f.nextDueDate,
        overdueEmis: overdueCount,
        remainingEmis: remainingCount,
        emiAmount: f.emiAmount,
      }),
    [f.nextDueDate, overdueCount, remainingCount, f.emiAmount, cutoverYmd]
  );

  const set = (patch: Partial<typeof f>) => setF((prev) => ({ ...prev, ...patch }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canWrite) return;
    const built = buildMigratedFinanceData({
      cutoverYmd,
      customerName: f.customerName,
      mobileNumber: f.mobileNumber,
      regNo: f.regNo,
      haNumber: f.haNumber,
      originalAgreementDate: f.originalAgreementDate,
      originalPrincipal: f.originalPrincipal,
      originalTenureMonths: f.originalTenureMonths,
      emisPaidBefore: f.emisPaidBefore,
      outstandingPrincipal: f.outstandingPrincipal,
      interestDue: f.interestDue,
      remainingEmis: remainingCount,
      overdueEmis: overdueCount,
      emiAmount: f.emiAmount,
      interestPercentage: f.interestPercentage,
      nextDueDate: f.nextDueDate,
      applicantPhoto: f.applicantPhoto,
      notes: f.notes,
    });
    if (!built.ok) {
      toast.error(built.error);
      return;
    }
    const overdueAtCutover = built.overdueAtCutover;

    upsertFinanceRecord({
      id: recordId,
      createdAt: adminEntryIso(cutoverYmd),
      updatedAt: new Date().toISOString(),
      source: 'local',
      data: built.data,
    });

    void readFinanceRecords();
    toast.success(
      overdueAtCutover
        ? `Finance loan migrated with ${overdueAtCutover} overdue EMI(s). They show in overdue and collections now.`
        : 'Finance loan migrated. Collections continue from the next due date.'
    );
    onDone();
  };

  return (
    <form onSubmit={submit} className="app-card app-card-inner space-y-5">
      <div className="flex items-center gap-2 text-emerald-800">
        <Wallet className="h-4 w-4" />
        <div>
          <h2 className="text-sm font-semibold text-zinc-900">{t('accounts.migrateFinanceTitle')}</h2>
          <p className="text-xs text-zinc-500">{t('accounts.migrateFinanceHint')}</p>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>{t('accounts.migrateWarning')}</span>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label={t('accounts.cutoverDate')}>
          <input type="date" className="app-field" value={f.cutoverDate} onChange={(e) => set({ cutoverDate: e.target.value })} required />
        </Field>
        <Field label="Customer name *">
          <input className="app-field" value={f.customerName} onChange={(e) => set({ customerName: e.target.value })} required />
        </Field>
        <Field label="Mobile *">
          <input className="app-field" value={f.mobileNumber} onChange={(e) => set({ mobileNumber: e.target.value })} required />
        </Field>
        <Field label="Reg. no.">
          <input className="app-field" value={f.regNo} onChange={(e) => set({ regNo: e.target.value })} />
        </Field>
        <Field label="HA number">
          <input className="app-field" value={f.haNumber} onChange={(e) => set({ haNumber: e.target.value })} />
        </Field>
        <PhotoField
          value={f.applicantPhoto}
          onChange={(v) => set({ applicantPhoto: v })}
          customerId={recordId}
        />

        <div className="md:col-span-2 border-t border-zinc-200 pt-3 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:border-zinc-700">
          Old loan details
        </div>
        <Field label="Original agreement date">
          <input type="date" className="app-field" value={f.originalAgreementDate} onChange={(e) => set({ originalAgreementDate: e.target.value })} />
        </Field>
        <Field label="Original principal (₹)">
          <input type="number" min={0} className="app-field" value={f.originalPrincipal} onChange={(e) => set({ originalPrincipal: e.target.value })} />
        </Field>
        <Field label="Original tenure (months)">
          <input type="number" min={0} className="app-field" value={f.originalTenureMonths} onChange={(e) => set({ originalTenureMonths: e.target.value })} />
        </Field>
        <Field label="EMIs already paid (before software)">
          <input type="number" min={0} className="app-field" value={f.emisPaidBefore} onChange={(e) => set({ emisPaidBefore: e.target.value })} />
        </Field>

        <div className="md:col-span-2 border-t border-zinc-200 pt-3 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:border-zinc-700">
          Position on the cutover date
        </div>
        <Field label="Outstanding principal (₹) *">
          <input type="number" min={0} className="app-field" value={f.outstandingPrincipal} onChange={(e) => set({ outstandingPrincipal: e.target.value })} required />
        </Field>
        <Field label="EMI amount (₹) *">
          <input type="number" min={0} className="app-field" value={f.emiAmount} onChange={(e) => set({ emiAmount: e.target.value })} required />
        </Field>
        <Field
          label="EMIs overdue (unpaid) at cutover"
          hint="EMIs that were already due but not paid. Leave 0 if the customer is up to date."
        >
          <input type="number" min={0} className="app-field" value={f.overdueEmis} onChange={(e) => set({ overdueEmis: e.target.value })} placeholder="0" />
        </Field>
        <Field
          label="Next EMI due date *"
          hint={
            overdueCount > 0
              ? `The next EMI falling due on or after the cutover date. The ${overdueCount} overdue EMI(s) are placed on the months before it.`
              : 'The next EMI the customer has to pay.'
          }
        >
          <input type="date" className="app-field" value={f.nextDueDate} onChange={(e) => set({ nextDueDate: e.target.value })} required />
        </Field>
        <Field
          label={`EMIs still to collect, including overdue *${remainingAuto ? ` (suggest ${remainingAuto})` : ''}`}
          hint="Overdue EMIs + EMIs not yet due."
        >
          <input
            type="number"
            min={1}
            className="app-field"
            value={f.remainingMonths || (remainingAuto ? String(remainingAuto) : '')}
            onChange={(e) => set({ remainingMonths: e.target.value })}
            required
          />
        </Field>
        <Field label="Interest (₹ / ₹100 / month)">
          <input type="number" min={0} step="any" className="app-field" value={f.interestPercentage} onChange={(e) => set({ interestPercentage: e.target.value })} />
        </Field>
        <Field
          label="Other charges due at cutover (₹)"
          hint="For reference only. Overdue EMIs already include their interest — don't add it here."
        >
          <input type="number" min={0} className="app-field" value={f.interestDue} onChange={(e) => set({ interestDue: e.target.value })} />
        </Field>
        <div className="md:col-span-2">
          <OverduePreview preview={preview} cutoverYmd={cutoverYmd} showPenalty />
        </div>
        <div className="md:col-span-2">
          <Field label="Notes">
            <textarea className="app-field min-h-[64px]" value={f.notes} onChange={(e) => set({ notes: e.target.value })} />
          </Field>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button type="submit" className="app-btn-primary" disabled={!canWrite}>
          <BookMarked className="mr-1.5 h-4 w-4" />
          {t('accounts.saveMigratedFinance')}
        </button>
      </div>
    </form>
  );
}

export function MigrateHandForm({
  canWrite,
  cutoverDefault,
  onDone,
}: {
  canWrite: boolean;
  cutoverDefault: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [recordId] = useState(() => crypto.randomUUID());
  const [f, setF] = useState({
    cutoverDate: cutoverDefault,
    name: '',
    mobile: '',
    loanType: 'PERSONAL',
    repaymentType: 'emi' as 'emi' | 'short_term',
    interestType: 'simple' as 'simple' | 'compound',
    interestRate: '',
    originalAgreementDate: '',
    originalPrincipal: '',
    outstandingPrincipal: '',
    openingInterestDue: '',
    loanTerm: '',
    emisPaidBefore: '',
    overdueEmis: '',
    nextDueDate: '',
    applicantPhoto: '',
    notes: '',
  });
  const set = (patch: Partial<typeof f>) => setF((prev) => ({ ...prev, ...patch }));

  const cutoverYmd = adminEntryDateOnly(f.cutoverDate);
  const isEmi = f.repaymentType === 'emi';
  const overdueCount = isEmi ? Math.floor(num(f.overdueEmis)) : 0;
  const months = Math.floor(num(f.loanTerm));
  const handEmi = isEmi ? handEmiForMigration({ outstandingPrincipal: f.outstandingPrincipal, interestRate: f.interestRate, remainingEmis: months }) : 0;

  const emiPreview = useMemo(
    () =>
      previewMigratedHand({
        cutoverYmd,
        repaymentType: f.repaymentType,
        nextDueDate: f.nextDueDate,
        overdueEmis: overdueCount,
        remainingEmis: months,
        outstandingPrincipal: f.outstandingPrincipal,
        interestRate: f.interestRate,
      }),
    [f.repaymentType, f.nextDueDate, overdueCount, months, f.outstandingPrincipal, f.interestRate, cutoverYmd]
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canWrite) return;
    const built = buildMigratedHandData({
      cutoverYmd,
      name: f.name,
      mobile: f.mobile,
      loanType: f.loanType,
      repaymentType: f.repaymentType,
      interestType: f.interestType,
      interestRate: f.interestRate,
      originalAgreementDate: f.originalAgreementDate,
      originalPrincipal: f.originalPrincipal,
      outstandingPrincipal: f.outstandingPrincipal,
      openingInterestDue: f.openingInterestDue,
      remainingEmis: f.loanTerm,
      emisPaidBefore: f.emisPaidBefore,
      overdueEmis: f.overdueEmis,
      nextDueDate: f.nextDueDate,
      applicantPhoto: f.applicantPhoto,
      notes: f.notes,
    });
    if (!built.ok) {
      toast.error(built.error);
      return;
    }
    const overdueAtCutover = built.overdueAtCutover;

    upsertHandLoanRecord({
      id: recordId,
      createdAt: adminEntryIso(cutoverYmd),
      updatedAt: new Date().toISOString(),
      source: 'local',
      data: built.data,
    });
    void readHandLoanRecords();
    toast.success(overdueAtCutover ? `Hand loan migrated with ${overdueAtCutover} overdue EMI(s).` : 'Hand loan migrated.');
    onDone();
  };

  const shortTermDays = !isEmi && f.nextDueDate && cutoverYmd ? daysBetweenYmd(cutoverYmd, f.nextDueDate) : 0;
  const shortTermPastDue = !isEmi && f.nextDueDate && cutoverYmd ? daysBetweenYmd(f.nextDueDate, cutoverYmd) : 0;

  return (
    <form onSubmit={submit} className="app-card app-card-inner space-y-5">
      <div className="flex items-center gap-2 text-emerald-800">
        <HandCoins className="h-4 w-4" />
        <div>
          <h2 className="text-sm font-semibold text-zinc-900">{t('accounts.migrateHandTitle')}</h2>
          <p className="text-xs text-zinc-500">{t('accounts.migrateHandHint')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label={t('accounts.cutoverDate')}>
          <input type="date" className="app-field" value={f.cutoverDate} onChange={(e) => set({ cutoverDate: e.target.value })} required />
        </Field>
        <Field label="Borrower name *">
          <input className="app-field" value={f.name} onChange={(e) => set({ name: e.target.value })} required />
        </Field>
        <Field label="Mobile / contact">
          <input className="app-field" value={f.mobile} onChange={(e) => set({ mobile: e.target.value })} />
        </Field>
        <Field label="Loan type">
          <select className="app-field" value={f.loanType} onChange={(e) => set({ loanType: e.target.value })}>
            <option value="PERSONAL">Personal</option>
            <option value="VEHICLE">Vehicle</option>
          </select>
        </Field>
        <PhotoField
          value={f.applicantPhoto}
          onChange={(v) => set({ applicantPhoto: v })}
          customerId={recordId}
        />

        <div className="md:col-span-2 border-t border-zinc-200 pt-3 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:border-zinc-700">
          Loan terms
        </div>
        <Field label="Repayment">
          <select
            className="app-field"
            value={f.repaymentType}
            onChange={(e) => set({ repaymentType: e.target.value as 'emi' | 'short_term' })}
          >
            <option value="emi">Monthly EMI</option>
            <option value="short_term">Short-term lump sum</option>
          </select>
        </Field>
        <Field label="Interest type">
          <select
            className="app-field"
            value={f.interestType}
            onChange={(e) => set({ interestType: e.target.value as 'simple' | 'compound' })}
          >
            <option value="simple">Simple</option>
            <option value="compound">Compound</option>
          </select>
        </Field>
        <Field label="Interest rate (₹ / ₹100 / month)">
          <input type="number" min={0} step="any" className="app-field" value={f.interestRate} onChange={(e) => set({ interestRate: e.target.value })} />
        </Field>
        <Field label="Original agreement date">
          <input type="date" className="app-field" value={f.originalAgreementDate} onChange={(e) => set({ originalAgreementDate: e.target.value })} />
        </Field>
        <Field label="Original principal (₹)">
          <input type="number" min={0} className="app-field" value={f.originalPrincipal} onChange={(e) => set({ originalPrincipal: e.target.value })} />
        </Field>

        <div className="md:col-span-2 border-t border-zinc-200 pt-3 text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:border-zinc-700">
          Position on the cutover date
        </div>
        <Field label="Outstanding principal (₹) *">
          <input type="number" min={0} className="app-field" value={f.outstandingPrincipal} onChange={(e) => set({ outstandingPrincipal: e.target.value })} required />
        </Field>
        <Field
          label="Interest due at cutover (₹)"
          hint={
            isEmi && overdueCount > 0
              ? 'The software adds the interest of the overdue EMIs from the oldest due date — enter only other interest still owed.'
              : 'Unpaid interest owed up to the cutover date. Interest after cutover is added by the software.'
          }
        >
          <input type="number" min={0} className="app-field" value={f.openingInterestDue} onChange={(e) => set({ openingInterestDue: e.target.value })} />
        </Field>
        {isEmi ? (
          <>
            <Field label="EMIs already paid">
              <input type="number" min={0} className="app-field" value={f.emisPaidBefore} onChange={(e) => set({ emisPaidBefore: e.target.value })} />
            </Field>
            <Field
              label="EMIs overdue (unpaid) at cutover"
              hint="EMIs that were already due but not paid. Leave 0 if the borrower is up to date."
            >
              <input type="number" min={0} className="app-field" value={f.overdueEmis} onChange={(e) => set({ overdueEmis: e.target.value })} placeholder="0" />
            </Field>
            <Field
              label="Next EMI due date *"
              hint={
                overdueCount > 0
                  ? `The next EMI falling due on or after the cutover date. The ${overdueCount} overdue EMI(s) are placed on the months before it.`
                  : 'The next EMI the borrower has to pay.'
              }
            >
              <input type="date" className="app-field" value={f.nextDueDate} onChange={(e) => set({ nextDueDate: e.target.value })} required />
            </Field>
            <Field label="EMIs still to collect, including overdue *" hint={handEmi ? `EMI in the software: ${inr(handEmi)}` : undefined}>
              <input type="number" min={1} className="app-field" value={f.loanTerm} onChange={(e) => set({ loanTerm: e.target.value })} required />
            </Field>
            <div className="md:col-span-2">
              <OverduePreview preview={emiPreview} cutoverYmd={cutoverYmd} showPenalty={false} />
            </div>
          </>
        ) : (
          <>
            <Field label="Repayment due date *" hint="When the lump sum is (or was) due.">
              <input type="date" className="app-field" value={f.nextDueDate} onChange={(e) => set({ nextDueDate: e.target.value })} required />
            </Field>
            <div className="md:col-span-2">
              {f.nextDueDate ? (
                shortTermPastDue > 0 ? (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-100">
                    Already {shortTermPastDue} days past due at cutover ({dateLabel(cutoverYmd)}). The software treats it as past its
                    term from the cutover date, so unpaid interest compounds from then. Enter interest owed up to cutover above.
                  </p>
                ) : (
                  <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:border-emerald-800/50 dark:bg-emerald-950/30 dark:text-emerald-100">
                    Due in {Math.max(1, shortTermDays)} days from cutover ({dateLabel(f.nextDueDate)}). Interest is added from the cutover
                    date.
                  </p>
                )
              ) : null}
            </div>
          </>
        )}
        <div className="md:col-span-2">
          <Field label="Notes">
            <textarea className="app-field min-h-[64px]" value={f.notes} onChange={(e) => set({ notes: e.target.value })} />
          </Field>
        </div>
      </div>

      <div className="flex justify-end">
        <button type="submit" className="app-btn-primary" disabled={!canWrite}>
          <BookMarked className="mr-1.5 h-4 w-4" />
          {t('accounts.saveMigratedHand')}
        </button>
      </div>
    </form>
  );
}
